"use client";

import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import TextInput from "../../common/FormFields/TextInput.jsx";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from '../../common/Toast/toast';
import { useParams, Link, useNavigate } from "react-router-dom";
import AsyncSelectDropdown from "../../common/CustomAsyncDropdown.jsx";
import { getappstoroles } from "../../../../services/AppToRole.js";
import { addUser, getUser, updateUser } from "../../../../services/UserSerivce.js";
import { useAuth } from "../../../../context/AuthContext";
import { mayEditPassword, prefillPassword } from "../../../../utils/passwordField.js";
import useScrollLoadMore from "../../common/useScrollLoadMore";
import "../../common/SettingsStepForm.css";

// Roles are fetched from the API this many at a time, until all are in.
const ROLE_FETCH_SIZE = 100;
// And shown this many at a time: the picker grows as it is scrolled.
const ROLE_BATCH = 12;

/* ── The two steps ──────────────────────────────────────────────────
   Six fields and a role picker. Split the way AddAttendance splits its
   own, and the way the other settings dialogs do: who the person is,
   then what they may do. The dialog titles itself from this, through
   onStepChange.
   ------------------------------------------------------------------ */
const STEP_COPY = [
  {
    title: "Who is this person?",
    hint: "Their name, how to reach them, and how they sign in.",
  },
  {
    title: "What may they do?",
    hint: "Every role ticked here is granted to the account.",
  },
];

const STEP_LABELS = ["Details", "Roles"];

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

const TickIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AddUser = ({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and toast host, since the dialog and the list supply
  // them. The card chrome around it is flattened by SettingsFormModal.css.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
  // Told the current step's question, so the dialog can title itself.
  onStepChange,
}) => {
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pwShown, setPwShown] = useState(false);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [filteredRolesOptions, setFilteredRolesOptions] = useState([]);
  const [userStatus, setUserStatus] = useState(""); // State to hold user status from API

  /* ── Who may set a password ───────────────────────────────────────
     Setting someone else's password is an admin action. Everyone else
     edits the rest of the account without ever seeing the field --
     `password` is then absent from the payload as well, so a non-admin
     cannot reach it by other means from this form. */
  const { user: currentUser } = useAuth();
  const maySetPassword = mayEditPassword(currentUser);

  /* What DEV_MODE put in the box, so an untouched prefill can be told
     from a password that was actually typed. Only the second is worth
     sending -- see handleSubmit. */
  const [prefilledPw, setPrefilledPw] = useState("");

  /* ── Steps ────────────────────────────────────────────────────────
     `step` is the one being asked for; `shownStep` is the one on
     screen, which trails it by one exit. Keying the stage on `step`
     alone swaps the content in a single frame -- the old step vanishes
     with no exit while the new one animates in from nothing, which is
     what reads as a jump. Same pair AddAttendance keeps.
     ---------------------------------------------------------------- */
  const [step, setStep] = useState(1);
  const [shownStep, setShownStep] = useState(1);
  const [stepPhase, setStepPhase] = useState("in");
  // What the picker's search box holds. Lives here rather than in the
  // step so it survives a trip back to step one.
  const [pickQuery, setPickQuery] = useState("");
  // How many of the matching roles are on screen. Scrolling to the foot of
  // the picker adds a batch; a new search starts again from the first.
  const [rolesShown, setRolesShown] = useState(ROLE_BATCH);
  const rolesEndRef = useRef(null);

  useEffect(() => {
    setRolesShown(ROLE_BATCH);
  }, [pickQuery]);

  useScrollLoadMore(
    rolesEndRef,
    () => setRolesShown((n) => (n < rolesOptions.length ? n + ROLE_BATCH : n)),
    [rolesShown, rolesOptions.length, shownStep, pickQuery],
    80,
  );

  useEffect(() => {
    if (shownStep === step) return undefined;
    setStepPhase("out");
    const t = setTimeout(() => {
      setShownStep(step);
      setStepPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  useEffect(() => {
    if (onStepChange) onStepChange(STEP_COPY[step - 1], step);
    // onStepChange is the dialog's setState; adding it here would
    // re-report on every render of the list behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const navigate = useNavigate();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate('/setting/List-user');
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate('/setting/List-user');
  };

  // Status options for the dropdown
  const statusOptions = [
    { value: "unverified", label: "Unverified" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" }
  ];

  /* Every role, not just the first page. This used to call
     getappstoroles() bare, which is page 1 of 10 -- so a role past the
     tenth could not be ticked, and one the account already held was
     counted ("3 of 10 selected") with no box to show for it. Same loop
     the user list runs for its roles column. */
  const fetchRoles = async () => {
    try {
      const all = [];
      let page = 1;
      let total = Infinity;
      let invalid = false;
      while (all.length < total) {
        const response = await getappstoroles(ROLE_FETCH_SIZE, page);
        if (!response.data || !Array.isArray(response.data)) {
          invalid = page === 1;
          break;
        }
        const batch = response.data;
        total = Number(response.pagination?.total ?? all.length + batch.length);
        all.push(...batch);
        page += 1;
        if (batch.length < ROLE_FETCH_SIZE) break; // short page -- no more left
      }

      if (!invalid) {
        const seen = new Set();
        const formattedRoles = all
          .filter((role) => !seen.has(role.id) && seen.add(role.id))
          .map((role) => ({
            label: role.label,
            value: role.value,
            id: role.id,
          }));

        setRolesOptions(formattedRoles);
        return formattedRoles;
      } else {
        console.warn("No roles data found or invalid format");
        setRolesOptions([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error(`Failed to fetch roles: ${error.message}`);
      setRolesOptions([]);
      throw error;
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([fetchRoles()]);
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();

    if (id) {
      setIsEditing(true);
      fetchUserDetails(id);
    } else {
      setUserData(null);
      setIsEditing(false);
      setUserStatus(""); // Reset status when adding new user
    }
  }, [id]);

  useEffect(() => {
    updateFilteredRoles();
  }, [rolesOptions, userData]);

  const updateFilteredRoles = (selectedApps = []) => {
    setFilteredRolesOptions(rolesOptions);
  };

  const fetchUserDetails = async (userId) => {
    setIsLoading(true);
    try {
      const response = await getUser(userId);
      if (response.data) {
        // Store the status from API
        const statusFromAPI = response.data.status || "";
        setUserStatus(statusFromAPI);
        
        /* Never the raw spread, whatever the response carried.
           ...response.data below is a spread of whatever the service
           returns, and if that ever includes the stored password the
           field would show the hash and submitting would send it back
           as a new password -- hashing the hash and locking the
           account out of its own credentials.

           prefillPassword answers it in one place: "" normally, which
           means "leave it alone", and the readable password only when
           DEV_MODE is on and what came back is not hash-shaped. A
           non-admin never gets it, since they never get the field. */
        const devPw = maySetPassword ? prefillPassword(response.data) : "";
        setPrefilledPw(devPw);

        const userData = {
          ...response.data,
          profileIds: Array.isArray(response.data.profileIds)
            ? response.data.profileIds
            : response.data.profileIds.split(','),
          // Include status in form data
          status: statusFromAPI,
          password: devPw
        };
        setUserData(userData);
        updateFilteredRoles();
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error(`Failed to fetch user details: ${error.message}`);
      setUserData(null);
      setUserStatus("");
    } finally {
      setIsLoading(false);
    }
  };

  // Updated validation schema - status is only required when editing
  /* Step one's rules on their own, and the whole form's built from
     them, so "may Continue?" is answered by the same rules that decide
     "may Save?" -- a hand-written copy of them on the button is a
     second, quieter schema that drifts the moment a rule changes here.
     Built inside the component because the status rule only applies
     when editing. */
  const stepOneSchema = Yup.object().shape({
    firstName: Yup.string().trim().required("First name is required"),
    lastName: Yup.string().trim().required("Last name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobileNumber: Yup.string()
      .matches(/^[0-9]+$/, "Must be only digits")
      .min(10, "Must be at least 10 digits")
      .required("Contact number is required"),
    // Only validate status when editing
    ...(isEditing && {
      status: Yup.string()
        .oneOf(["unverified", "active", "inactive"], "Invalid status")
        .required("Status is required"),
    }),
    /* Optional on an edit -- blank means "leave the password alone".
       Checked only once something has been typed, so the rule cannot
       block a save that was never about the password, and only for the
       admin who has the field: a rule on a box nobody can see is a save
       that fails with its error off screen.

       A DEV_MODE prefill is exempt. It is an existing password, it is
       not being changed, and it may well be shorter than the rule --
       holding the whole form hostage over it would be a rule about a
       value the form is not even going to send. */
    ...(isEditing && maySetPassword && {
      password: Yup.string()
        .transform((v) => (typeof v === "string" && v.trim() === "" ? undefined : v))
        .test(
          "pw-length",
          "Use at least 8 characters",
          (v) => v === undefined || v === prefilledPw || String(v).length >= 8
        )
    })
  });

  const validationSchema = stepOneSchema.shape({
    profileIds: Yup.array().min(1, "At least one role must be selected"),
  });

  const initialValues = userData || {
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
    profileIds: [],
   
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // Prepare payload - only include status when editing
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        mobileNumber: values.mobileNumber,
        profileIds: values.profileIds.join(','),
        status:values.status,
      };

      /* `password` is sent only when one was actually typed, on a new
         account or an existing one.

         It used to sit in the object literal above as
         `password: values.password`, which meant every edit posted
         password: "" -- the line below it then re-added it for creates
         only, as though the first line were not there. An empty string
         is not "no change", it is a value, and whether the service
         ignored it or wrote it was down to the service. Now the key is
         absent unless there is something to set. */
      const newPassword = String(values.password || "").trim();
      /* Three ways the key stays off the payload:
         - nothing was typed (the ordinary edit, "leave it alone");
         - an edit by a non-admin, who never saw the field, so any value
           on `values` is stale rather than intended. Only the edit is
           gated: the password set while creating an account is part of
           creating it, and is left to whoever may create one;
         - DEV_MODE prefilled the box and it was left exactly as it came
           back, which is not a change and does not need re-sending. */
      const untouchedPrefill = Boolean(prefilledPw) && newPassword === prefilledPw;
      const mayPost = !isEditing || maySetPassword;
      if (newPassword && mayPost && !untouchedPrefill) {
        payload.password = newPassword;
      }

      // Only include status when editing
      if (isEditing) {
        // Use the updated status value from form
        payload.status = values.status || userStatus;
      }

      if (isEditing && id) {
        await updateUser(payload, id);
        toast.success('User updated successfully!');
      } else {
        await addUser(payload);
        toast.success('User added successfully!');
      }

      resetForm();
      finish();
    } catch (error) {
      console.error('Error submitting user:', error);
      // auth-api explains a refusal (e.g. a duplicate email or mobile
      // number) in message -- a string, or { message } from its locales.
      const serverMessage = error?.response?.data?.message;
      toast.error(
        (typeof serverMessage === "string" ? serverMessage : serverMessage?.message) ||
          `Error processing user: ${error.message}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const refreshRoles = async () => {
    try {
      await fetchRoles();
      // A refresh, not a saved change: it toasts even inside the dialog.
      toast.success("Roles refreshed successfully!", { toast: true });
    } catch (error) {
      console.error("Error refreshing roles:", error);
      toast.error("Failed to refresh roles");
    }
  };

  if (isLoading) {
    return <div className="text-center p-5">Loading user data...</div>;
  }

  /* ── The dialog build ───────────────────────────────────────────────
     Two steps, the shape AddAttendance uses and the other settings
     dialogs share. The stepper, the stage and its transition are the
     same `att-*` classes; everything inside a step is the `sf-*`
     language in common/SettingsStepForm.css.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="sf">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            submitForm,
            isSubmitting,
          }) => {
            const bad = (name) => touched[name] && errors[name];

            const chosen = new Set((values.profileIds || []).map(String));
            // The roles the account already holds come first, so opening
            // the picker shows what is ticked without scrolling for it.
            // Ordered by what was SAVED, not by what is ticked now -- a box
            // must not jump up the list the moment it is clicked.
            const saved = new Set((userData?.profileIds || []).map(String));
            const ordered = [
              ...filteredRolesOptions.filter((r) => saved.has(String(r.id))),
              ...filteredRolesOptions.filter((r) => !saved.has(String(r.id))),
            ];
            const shown = pickQuery.trim()
              ? ordered.filter((r) =>
                  String(r.label || "")
                    .toLowerCase()
                    .includes(pickQuery.trim().toLowerCase())
                )
              : ordered;
            // What is on screen so far; the rest arrives on scroll.
            const visible = shown.slice(0, rolesShown);

            const toggle = (rid) => {
              setFieldTouched("profileIds", true, false);
              const next = chosen.has(rid)
                ? (values.profileIds || []).filter((v) => String(v) !== rid)
                : [...(values.profileIds || []), rid];
              setFieldValue("profileIds", next);
            };

            // Bulk actions act on what the search is showing, not on the
            // whole list -- "Select all" under a filter that hides most of
            // it, silently ticking the hidden ones, is a trap.
            const shownIds = shown.map((r) => String(r.id));
            const allShownOn =
              shownIds.length > 0 && shownIds.every((rid) => chosen.has(rid));

            const selectShown = () => {
              setFieldTouched("profileIds", true, false);
              setFieldValue("profileIds", [
                ...new Set([...(values.profileIds || []).map(String), ...shownIds]),
              ]);
            };

            const clearShown = () => {
              setFieldTouched("profileIds", true, false);
              const drop = new Set(shownIds);
              setFieldValue(
                "profileIds",
                (values.profileIds || []).filter((v) => !drop.has(String(v)))
              );
            };

            // Step one is answered when its own fields pass their own
            // rules -- the same object the full schema is built from, so
            // there is no second copy of them here to fall out of step.
            const stepOneOk = stepOneSchema.isValidSync(values);

            const goToTwo = () => {
              ["firstName", "lastName", "email", "mobileNumber"].forEach((n) =>
                setFieldTouched(n, true, true)
              );
              if (isEditing) setFieldTouched("status", true, true);
              if (stepOneOk) setStep(2);
            };

            const save = () => {
              setFieldTouched("profileIds", true, true);
              // Saving with a step-one field invalid would fail validation
              // silently -- its error lives on a step that is not on
              // screen. Go back and show it.
              if (!stepOneOk) {
                setStep(1);
                return;
              }
              submitForm();
            };

            const field = (name, label, extra = {}) => (
              <div className="sf-field">
                <div className="sf-label-row">
                  <label className="sf-label" htmlFor={`usr-${name}`}>
                    {label}
                    {extra.required !== false && <span className="sf-req">*</span>}
                  </label>
                </div>
                <input
                  id={`usr-${name}`}
                  name={name}
                  type={extra.type || "text"}
                  className={`sf-input${bad(name) ? " sf-input--bad" : ""}`}
                  value={values[name] || ""}
                  placeholder={extra.placeholder}
                  autoComplete={extra.autoComplete || "off"}
                  inputMode={extra.inputMode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {bad(name) ? (
                  <p className="sf-error"><WarnIcon />{errors[name]}</p>
                ) : (
                  extra.hint && <p className="sf-hint">{extra.hint}</p>
                )}
              </div>
            );

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing
                  // that reaches this is Enter pressed inside a field. On
                  // step one that key means "continue".
                  e.preventDefault();
                  if (step !== 2) {
                    goToTwo();
                    return;
                  }
                  save();
                }}
              >
                <ol className="att-steps">
                  {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    const state = n === step ? "is-now" : n < step ? "is-done" : "";
                    return (
                      <li className={`att-step-tab ${state}`.trim()} key={label}>
                        <button
                          type="button"
                          // Adding: only backwards, a step ahead has not been
                          // answered yet. Editing: every step is filled, so any tab opens.
                          disabled={n === step || (!isEditing && n > step)}
                          onClick={() => setStep(n)}
                        >
                          <span className="att-step-n">{n}</span>
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ol>

                {/* Painted from shownStep, not step -- see the comment on
                    the transition effect above. */}
                <div className={`att-stage is-${stepPhase}`}>
                  <div className="att-stage-inner" key={shownStep}>
                    {shownStep === 1 && (
                      <section className="att-step">
                        <div className="sf-row">
                          {field("firstName", "First name", { placeholder: "Asha" })}
                          {field("lastName", "Last name", { placeholder: "Kulkarni" })}
                        </div>

                        <div className="sf-row">
                          {field("email", "Email", {
                            type: "email",
                            placeholder: "asha@example.com",
                            hint: "Also the username they sign in with.",
                          })}
                          {field("mobileNumber", "Contact number", {
                            inputMode: "numeric",
                            placeholder: "9876543210",
                            hint: "Digits only, at least ten.",
                          })}
                        </div>

                        {/* Password on a new account, status on an existing
                            one -- an account cannot be given both, and the
                            page showed both regardless of which it was. */}
                        {isEditing ? (
                          <>
                          <div className="sf-field">
                            <div className="sf-label-row">
                              <label className="sf-label" htmlFor="usr-status">
                                Status<span className="sf-req">*</span>
                              </label>
                            </div>
                            <select
                              id="usr-status"
                              name="status"
                              className={`sf-input${bad("status") ? " sf-input--bad" : ""}`}
                              value={values.status || userStatus || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <option value="">Select a status…</option>
                              {statusOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            {bad("status") ? (
                              <p className="sf-error"><WarnIcon />{errors.status}</p>
                            ) : (
                              <p className="sf-hint">
                                Inactive keeps the account but refuses the sign-in.
                              </p>
                            )}
                          </div>

                          {/* Set a password directly. Admins only -- for
                              anyone else the field is not rendered at all,
                              and handleSubmit will not post the key either.

                              Blank is the normal case and means "leave it
                              alone" -- the field cannot show the current
                              one (it is hashed) so an empty box must not
                              read as "no password". Only a non-empty value
                              is sent, and handleSubmit drops the key
                              entirely otherwise. Under DEV_MODE the box
                              arrives already filled, when the record came
                              back carrying a readable password. */}
                          {maySetPassword && (
                          <div className="sf-field usr-pw">
                            <div className="sf-label-row">
                              <label className="sf-label" htmlFor="usr-password">
                                New password
                              </label>
                              {/* Reveal rather than a confirm field: whoever
                                  sets someone else's password has to pass it
                                  on, so being able to read what was typed
                                  catches the typo a second box would only
                                  detect. */}
                              <button
                                type="button"
                                className="usr-pw-peek"
                                onClick={() => setPwShown((s) => !s)}
                              >
                                {pwShown ? "Hide" : "Show"}
                              </button>
                            </div>
                            <input
                              id="usr-password"
                              name="password"
                              type={pwShown ? "text" : "password"}
                              className={`sf-input${bad("password") ? " sf-input--bad" : ""}`}
                              value={values.password || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              autoComplete="new-password"
                              placeholder="Leave blank to keep the current one"
                            />
                            {bad("password") ? (
                              <p className="sf-error"><WarnIcon />{errors.password}</p>
                            ) : prefilledPw ? (
                              <p className="sf-hint">
                                DEV_MODE: this is the account&rsquo;s current password.
                                Leave it as it is to keep it, or type over it to change it.
                              </p>
                            ) : (
                              <p className="sf-hint">
                                Replaces this account&rsquo;s password immediately.
                                They are not told &mdash; you will need to pass it on.
                              </p>
                            )}
                          </div>
                          )}
                          </>
                        ) : (
                          field("password", "Password", {
                            type: "password",
                            required: false,
                            autoComplete: "new-password",
                            placeholder: "Leave blank to send an invitation",
                            hint: "Set one now, or leave it blank and let them choose it.",
                          })
                        )}
                      </section>
                    )}

                    {shownStep === 2 && (
                      <section className="att-step">
                        <p className="sf-step-note">
                          A role carries the applications and operations the
                          account may use. At least one is required.
                        </p>

                        <div className="sf-pick-bar">
                          <div className="sf-search">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                              <circle cx="11" cy="11" r="7" />
                              <path d="m20 20-3.5-3.5" />
                            </svg>
                            <input
                              type="text"
                              className="sf-search-input"
                              value={pickQuery}
                              placeholder="Search roles…"
                              autoComplete="off"
                              onChange={(e) => setPickQuery(e.target.value)}
                            />
                          </div>

                          <button
                            type="button"
                            className="sf-bulk"
                            onClick={allShownOn ? clearShown : selectShown}
                            disabled={shownIds.length === 0}
                          >
                            {allShownOn ? "Clear" : "Select"}
                            {pickQuery.trim() ? " shown" : " all"}
                          </button>
                        </div>

                        <p className="sf-tally">
                          <b>{(values.profileIds || []).length}</b> of{" "}
                          {filteredRolesOptions.length} selected
                          {pickQuery.trim() && ` · ${shown.length} shown`}
                        </p>

                        {shown.length === 0 ? (
                          <div className="sf-empty">
                            {filteredRolesOptions.length === 0
                              ? "No roles exist yet."
                              : `Nothing matches “${pickQuery.trim()}”.`}
                          </div>
                        ) : (
                          <ul className="sf-picklist">
                            {visible.map((role) => {
                              const rid = String(role.id);
                              const on = chosen.has(rid);
                              return (
                                <li key={rid}>
                                  <button
                                    type="button"
                                    className={`sf-pick${on ? " is-on" : ""}`}
                                    aria-pressed={on}
                                    onClick={() => toggle(rid)}
                                  >
                                    <span className="sf-box" aria-hidden="true">
                                      <TickIcon />
                                    </span>
                                    <span className="sf-pick-name">{role.label}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {/* The foot of the picker: coming into view is what
                            asks for the next batch (useScrollLoadMore). */}
                        {visible.length < shown.length && (
                          <p className="sf-hint" ref={rolesEndRef}>
                            {visible.length} of {shown.length} roles shown — scroll for more
                          </p>
                        )}

                        {touched.profileIds && errors.profileIds && (
                          <p className="sf-error"><WarnIcon />{errors.profileIds}</p>
                        )}
                      </section>
                    )}
                  </div>
                </div>

                <footer className="sf-foot">
                  {/* Keyed, and neither is type="submit". Two buttons at one
                      position are otherwise reconciled onto a single DOM
                      node: clicking Continue would flush step=2 before the
                      browser reached the click's default action, by which
                      time that same node was a submit button inside a form
                      -- and the record saved from step one. */}
                  {step > 1 ? (
                    <button key="back" type="button" className="sf-btn" onClick={() => setStep(1)}>
                      Back
                    </button>
                  ) : (
                    <button key="cancel" type="button" className="sf-btn" onClick={leave}>
                      Cancel
                    </button>
                  )}

                  <span className="sf-foot-gap" />

                  {step === 1 ? (
                    <button
                      key="continue"
                      type="button"
                      className="sf-btn sf-btn--main"
                      onClick={goToTwo}
                      disabled={!stepOneOk}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      key="save"
                      type="button"
                      className="sf-btn sf-btn--main"
                      disabled={isSubmitting || (values.profileIds || []).length === 0}
                      onClick={save}
                    >
                      {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                      {isSubmitting
                        ? isEditing ? "Updating…" : "Saving…"
                        : isEditing ? "Update user" : "Create user"}
                    </button>
                  )}
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <div>
      {/* Page-only: the list behind the dialog already has a toast host,
          and two would show every message twice. */}
      {/* Page-only: the dialog carries its own heading, and this link
          would navigate out from under it. */}
      {!embedded && (
        <>
          <Link to="/setting/List-user">
            <Button variant="primary mb-4">Go to User List</Button>
          </Link>
          <h4>{isEditing ? "Edit User" : "Add User"}</h4>
        </>
      )}

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form className="p-4" onSubmit={handleSubmit}>
            <Card className="p-4">
              <Row className="mb-3 me-3">
                <Col md={5} className="me-5">
                  <TextInput
                    label="First Name"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.firstName && errors.firstName}
                    errorMessage={errors.firstName}
                  />
                </Col>
                <Col md={5}>
                  <TextInput
                    label="Last Name"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.lastName && errors.lastName}
                    errorMessage={errors.lastName}
                  />
                </Col>
              </Row>
              <Row className="mb-3 me-3">
                <Col md={5} className="me-5">
                  <TextInput
                    label="Email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && errors.email}
                    errorMessage={errors.email}
                  />
                </Col>
                <Col md={5}>
                  <TextInput
                    label="Mobile Number"
                    name="mobileNumber"
                    value={values.mobileNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.mobileNumber && errors.mobileNumber}
                    errorMessage={errors.mobileNumber}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                {/* Setting a password on an account that exists is an admin
                    action, the same rule the dialog applies. Creating one
                    is not gated -- that password is part of creating the
                    account. The comment here used to say "only when adding
                    new user" with nothing enforcing it, so /setting/edituser/:id
                    handed the field to everyone. */}
                {(!isEditing || maySetPassword) && (
                  <Col md={5} className="me-5">
                    <TextInput
                      label="Password"
                      name="password"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.password && errors.password}
                      errorMessage={errors.password}
                    />
                  </Col>
                )}


                {/* Status dropdown - only show when editing */}
                  <Col md={5} className="me-5">
                    <Form.Group controlId="status">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        name="status"
                        value={values.status || userStatus || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.status && errors.status}
                      >
                        <option value="">Select Status</option>
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                      {touched.status && errors.status && (
                        <Form.Control.Feedback type="invalid">
                          {errors.status}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                

                <Col md={5} className="me-5">
                  <label>Roles</label>
                  <AsyncSelectDropdown
                    options={filteredRolesOptions}
                    value={(values.profileIds || []).map((id) => {
                      const role = rolesOptions.find(
                        (option) => option.id === id
                      );
                      return role || null;
                    })}
                    onChange={(selectedOptions) => {
                      const selectedValues = Array.isArray(selectedOptions)
                        ? selectedOptions.map((option) => option.id)
                        : selectedOptions
                        ? [selectedOptions.id]
                        : [];
                      setFieldValue("profileIds", selectedValues);
                    }}
                    placeholder="Select profiles"
                    isMulti
                    onRefresh={refreshRoles}
                  />
                  {touched.profileIds && errors.profileIds && (
                    <div className="text-danger">{errors.profileIds}</div>
                  )}
                </Col>
              </Row>

              <Row className="mt-3">
                <Col md={3} className="text-start">
                  <Button variant="danger" size="sm" type="button" onClick={leave}>
                    Cancel
                  </Button>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="success"
                    type="submit"
                    disabled={isSubmitting}
                    className="me-3"
                  >
                    {isSubmitting
                      ? "Processing..."
                      : isEditing
                      ? "Update"
                      : "Submit"}
                  </Button>
                </Col>
              </Row>
            </Card>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddUser;
